/**
 * Site settings slice
 * Loads and stores global contact settings used across the public site.
 * @module features/site-settings/siteSettingsSlice
 */

import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import api from '../../app/axios';

export const DEFAULT_SITE_SETTINGS = {
  supportEmail: 'info@pearlvoyages.com',
  supportPhone: '+46 70 468 30 05',
  contactAddress: 'Furuvägen 14, SE-472 32 Svanesund',
  whatsappLabel: 'Chat on WhatsApp',
  whatsappMessage: 'Hello Pearl Voyages, I would like to enquire about a trip.',
  ga4MeasurementId: '',
  articleCategories: [],
};

export const fetchSiteSettings = createAsyncThunk(
  'siteSettings/fetch',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get('/site-settings');
      return data.settings || {};
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to load site settings');
    }
  }
);

export const saveSiteSettings = createAsyncThunk(
  'siteSettings/save',
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await api.put('/site-settings', payload);
      return data.settings || {};
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to save site settings');
    }
  }
);

const siteSettingsSlice = createSlice({
  name: 'siteSettings',
  initialState: {
    settings: DEFAULT_SITE_SETTINGS,
    isLoading: false,
    isSaving: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchSiteSettings.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchSiteSettings.fulfilled, (state, action) => {
        state.isLoading = false;
        state.settings = { ...DEFAULT_SITE_SETTINGS, ...action.payload };
      })
      .addCase(fetchSiteSettings.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(saveSiteSettings.pending, (state) => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(saveSiteSettings.fulfilled, (state, action) => {
        state.isSaving = false;
        state.settings = { ...DEFAULT_SITE_SETTINGS, ...action.payload };
      })
      .addCase(saveSiteSettings.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload;
      });
  },
});

export default siteSettingsSlice.reducer;
